import { AnimatePresence } from "motion/react";
import { ReactElement, ReactNode, useState } from "react";

import {
  POLYLINE_TOOL_TOOLTIP_TRANSITION,
  POLYLINE_TOOL_TOOLTIP_VARIANTS,
} from "./polyline-tool-button.constants";
import {
  PolylineToolButtonStyled,
  PolylineToolButtonTooltip,
  PolylineToolButtonWrapper,
} from "./polyline-tool-button.styled";

interface IPolylineToolButtonProps {
  icon: ReactNode;
  /** Text shown in the hover tooltip (and as the accessible label). */
  label: string;
  /** The action's semantic accent — icon color on hover, fill when armed. */
  color: string;
  onClick: () => void;
  className?: string;
  /** Marks a toggle tool as on — fills the button and sets `aria-pressed`. */
  isActive?: boolean;
  /** Keeps the icon tinted at rest (e.g. manual polyline already saved on course). */
  isHighlighted?: boolean;
}

/**
 * A quiet ghost icon button for the map toolbar: neutral at rest, tinting to the
 * action's accent on hover, with a small dark tooltip (framer-motion) on hover.
 */
export const PolylineToolButton = ({
  icon,
  label,
  color,
  onClick,
  className,
  isActive,
  isHighlighted,
}: IPolylineToolButtonProps): ReactElement => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <PolylineToolButtonWrapper
      $active={isHovered}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <PolylineToolButtonStyled
        type="button"
        $color={color}
        $isActive={isActive}
        $isHighlighted={isHighlighted}
        className={className}
        aria-label={label}
        aria-pressed={isActive}
        onClick={onClick}
      >
        {icon}
      </PolylineToolButtonStyled>

      <AnimatePresence>
        {isHovered && (
          <PolylineToolButtonTooltip
            role="tooltip"
            variants={POLYLINE_TOOL_TOOLTIP_VARIANTS}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={POLYLINE_TOOL_TOOLTIP_TRANSITION}
          >
            {label}
          </PolylineToolButtonTooltip>
        )}
      </AnimatePresence>
    </PolylineToolButtonWrapper>
  );
};
